import { useState, useEffect, useRef } from "react";
import { api, ImportOrderAttachment } from "../api";
import { toast } from "./Toast";

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB

function mimeIcon(mime: string) {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "picture_as_pdf";
  return "attach_file";
}

export default function ImportAttachments({ orderId }: { orderId: number }) {
  const [attachments, setAttachments] = useState<ImportOrderAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const r = await api.importOrders.attachments.list(orderId);
      setAttachments(r.attachments || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [orderId]);

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log("[ImportAttachments] files:", files ? Array.from(files).map(f => ({ name: f.name, type: f.type, size: f.size })) : null, "orderId:", orderId);
    if (!files?.length) {
      toast.error("Không có file nào được chọn — thử lại");
      return;
    }

    setUploading(true);
    let successCount = 0;
    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" quá lớn, tối đa 8MB`);
        continue;
      }
      try {
        const base64 = await readFileBase64(file);
        console.log("[ImportAttachments] base64 len:", base64?.length, "mime:", file.type);
        await api.importOrders.attachments.upload(orderId, {
          file_name: file.name,
          base64,
          mime_type: file.type || (file.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream"),
        });
        successCount++;
      } catch (err: unknown) {
        const msg = (err as Error)?.message || "Lỗi upload";
        console.error("[ImportAttachments] upload error:", err);
        toast.error(`${file.name}: ${msg}`);
      }
    }
    if (successCount > 0) {
      toast.success(`Đã đính kèm ${successCount} file`);
      await load();
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleRemove = async (att: ImportOrderAttachment) => {
    try {
      await api.importOrders.attachments.remove(orderId, att.id);
      setAttachments((prev) => prev.filter((a) => a.id !== att.id));
      toast.success("Đã xóa chứng từ");
    } catch (err: unknown) {
      toast.error((err as Error)?.message || "Lỗi xóa");
    }
  };

  return (
    <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13 }}>
          <span style={{ marginRight: 4 }}>📎</span>Chứng từ
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ fontSize: 12, padding: "5px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
            {uploading ? "hourglass_top" : "attach_file"}
          </span>
          {uploading ? "Đang tải…" : "Đính kèm"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          style={{ display: "none" }}
          onChange={handleFiles}
        />
      </div>

      {loading && <div style={{ fontSize: 12, opacity: 0.6 }}>Đang tải…</div>}

      {!loading && attachments.length === 0 && (
        <div style={{ fontSize: 12, opacity: 0.5, fontStyle: "italic" }}>Chưa có chứng từ đính kèm</div>
      )}

      {attachments.length > 0 && (
        <div className="list" style={{ gap: 0 }}>
          {attachments.map((att) => (
            <div
              key={att.id}
              className="list-item"
              style={{ padding: "8px 4px", display: "flex", alignItems: "center", gap: 8 }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, color: att.mime_type === "application/pdf" ? "#ef4444" : "#60a5fa" }}>
                {mimeIcon(att.mime_type)}
              </span>
              <a
                href={att.drive_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ flex: 1, fontSize: 12.5, color: "var(--text-primary)", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={att.file_name}
              >
                {att.file_name}
              </a>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: 12, padding: "4px 6px", color: "var(--accent-red)", minHeight: 28 }}
                onClick={() => handleRemove(att)}
                title="Xóa chứng từ"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = () => reject(new Error("Lỗi đọc file"));
    reader.readAsDataURL(file);
  });
}
